import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageWrapper from "../pageWrapper";

//CSS
import "../0PageStyles.css";

//Components
import PageLoader from "../../Components/Other Components/pageLoader";
import Title from "../../Components/Other Components/Title";
import ItemCardHeader from "../../Components/Item Components/itemCardHeader";
import ItemCard from "../../Components/Item Components/itemCard";

//Functions and hooks
import { GetLowStockItems } from "../../BackendAccess/ItemHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";

export default function LowStockPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  const navigate = useNavigate();
  const { t } = useTranslation("items");

  useEffect(() => {
    fetchLowStockItems();
  }, []);

  const fetchLowStockItems = async () => {
    setLoading(true);
    try {
      const [data, error] = await GetLowStockItems();
      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        setItems(data);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (id) => {
    navigate("/workspace/items/itemEditPage", { state: { id } });
  };

  const headerProps = [
    { Value: t("ID"), columnWidth: 1 },
    { Value: t("Name"), columnWidth: 3 },
    { Value: t("Current Stock"), columnWidth: 2 },
    { Value: t("Minimum Stock"), columnWidth: 2 },
  ];

  const createCardProps = (item) => [
    { Value: item.ItemID, columnWidth: 1 },
    { Value: item.Name, columnWidth: 3 },
    { Value: `${item.Amount} ${item.Unit || ""}`, columnWidth: 2 },
    { Value: `${item.MinStock} ${item.Unit || ""}`, columnWidth: 2 },
  ];

  const tutorialSteps = [
    {
      target: null,
      title: t("Low stock warnings", { ns: "tutorial" }),
      message: t(
        "This page lists every item whose total stock, across all warehouses combined, has dropped below the minimum stock level you set for it. Keep an eye on it to know what needs reordering. You can set or change an item's minimum stock from its edit page.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".item-card-container .Card:first-child",
      title: t("Low stock items", { ns: "tutorial" }),
      message: t(
        "Each row is an item that's running low, with its current stock and the minimum you configured for it side by side. Click Edit to restock it or adjust its threshold.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="lowStock" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <Title className="mt-3">{t("Low Stock")}</Title>
          <div className="item-card-and-header-container w-90">
            {items.length > 0 && (
              <ItemCardHeader
                propertiesToShow={headerProps}
                bBtnVisible={false}
                bBtnSectionVisible={true}
              />
            )}
            <div className="item-card-container">
              {items.length === 0 ? (
                <p className="cardPlaceholder">
                  {t("No items are below their minimum stock level.")}
                </p>
              ) : (
                items.map((item) => (
                  <ItemCard
                    key={item.ItemID}
                    ItemID={item.ItemID}
                    propertiesToShow={createCardProps(item)}
                    bBtnVisible={true}
                    bBtnSectionVisible={true}
                    bBodyClickable={true}
                    onClick={() => handleEditClick(item.ItemID)}
                    btnText={t("Edit")}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
